import { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import { useDroppable, useDndMonitor, useDraggable } from '@dnd-kit/core';
import type { NumberLineItem } from '../../../../transformers/zod-schemas';
import { type DragOptionData } from '../../../dnd';
import { HighlightBands } from './HighlightBands';
import { CursorBalls, type CursorValidationStatuses } from './CursorBalls';
import {
  toPercent,
  pixelToValue,
  getSnapTicks,
  snapToNearest,
  getMinorTickValues,
  getPositionWidgetId,
  cleanFloat,
  almostEqual,
} from '../../../utils/number-line.utils';
import { encodeNLDragValue } from '../../../../utils/number-line.utils';
import type { UserAnswerValue } from '../../../types';

export interface FreeDragAxisProps {
  axisDropId: string;
  min: number;
  max: number;
  minorTicks: number;
  majorTicks: number[];
  showArrowLeft: boolean;
  showArrowRight: boolean;
  positions: NumberLineItem['positions'];
  values: Record<string, UserAnswerValue>;
  onChange: (widgetId: string, value: UserAnswerValue, fromDrop?: boolean, sourceOptionId?: string) => void;
  highlights?: NumberLineItem['highlights'];
  cursors?: NumberLineItem['cursors'];
  cursorValidationStatuses?: CursorValidationStatuses;
  /** Increments/decrements whenever the snap grid changes (driven by counterCount).
   *  FreeDragAxis uses this to discard stale chip positions on grid change. */
  snapKey: number;
}

// PlacedChip

interface PlacedChipProps {
  widgetId: string;
  label: string;
  leftPercent: number;
  isLanding: boolean;
  onRemove: () => void;
}

const PlacedChip: React.FC<PlacedChipProps> = ({ widgetId, label, leftPercent, isLanding, onRemove }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: widgetId,
    data: { type: 'placed-chip' as const, widgetId, label },
  });

  return (
    <div
      className={`number-line-placed-chip${isLanding ? ' nl-chip-landing' : ''}${isDragging ? ' nl-chip-dragging' : ''}`}
      style={{ left: `${leftPercent}%` }}
    >
      <div className="number-line-placed-chip-inner">
        <button
          ref={setNodeRef}
          className="number-line-placed-chip-btn"
          aria-label={`${label}, drag to move`}
          {...listeners}
          {...attributes}
        >
          {label}
        </button>
        <button
          type="button"
          className="number-line-placed-chip-remove"
          aria-label="Remove"
          onClick={onRemove}
        >
          ✕
        </button>
      </div>
    </div>
  );
};

// Types

interface PlacedChipState {
  label:    string;
  position: number;
  sourceId: string; // original drag-option id — keeps bank slot hidden
}

/** Nearest unoccupied widget slot to `axisValue`. Checks `values` + `placedChips`
 *  to block double-drops. `excludeWid` = chip being re-dragged (treated as free). */
export function findNearestFreeWidgetSlot(
  positions:   NumberLineItem['positions'],
  axisValue:   number,
  values:      Record<string, UserAnswerValue>,
  placedChips: Map<string, PlacedChipState>,
  excludeWid?: string,
): NumberLineItem['positions'][number] | undefined {
  let best: NumberLineItem['positions'][number] | undefined;
  let bestDist = Infinity;
  for (const pos of positions) {
    const wid = getPositionWidgetId(pos.content);
    if (!wid) continue;
    if (wid !== excludeWid && (values[wid] || placedChips.has(wid))) continue;
    const dist = Math.abs(pos.value - axisValue);
    if (dist < bestDist) { bestDist = dist; best = pos; }
  }
  return best;
}

// FreeDragAxis

/** Axis drop zone for free-drag mode. Chips snap to the nearest tick on release. */
export const FreeDragAxis: React.FC<FreeDragAxisProps> = ({
  axisDropId,
  min, max,
  minorTicks, majorTicks,
  showArrowLeft, showArrowRight,
  positions, values, onChange,
  highlights,
  cursors,
  cursorValidationStatuses,
  snapKey,
}) => {
  const axisRef = useRef<HTMLDivElement | null>(null);

  const snapTicks = useMemo(
    () => getSnapTicks(majorTicks, minorTicks),
    [majorTicks, minorTicks],
  );

  const minorValues = useMemo(
    () => getMinorTickValues(majorTicks, minorTicks),
    [majorTicks, minorTicks],
  );

  const [placedChips, setPlacedChips] = useState<Map<string, PlacedChipState>>(new Map());
  const [hoverSnap, setHoverSnap]     = useState<number | null>(null);
  const [landingChips, setLandingChips] = useState<Set<string>>(new Set());
  const landingTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => () => landingTimersRef.current.forEach(clearTimeout), []); // cancel on unmount
  const onChangeRef    = useRef(onChange);   // stable refs — avoid stale closures in monitor
  const placedChipsRef = useRef(placedChips);
  useEffect(() => { onChangeRef.current    = onChange;     }, [onChange]);
  useEffect(() => { placedChipsRef.current = placedChips; }, [placedChips]);

  // snap grid changed — discard stale chips; guard skips initial mount
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    for (const [widgetId, chip] of placedChipsRef.current) {
      onChangeRef.current(widgetId, '', false, chip.sourceId);
    }
    landingTimersRef.current.forEach(clearTimeout);
    landingTimersRef.current.clear();
    // sync ref before setState to prevent any race
    const empty = new Map<string, PlacedChipState>();
    placedChipsRef.current = empty;
    setPlacedChips(empty);
    setHoverSnap(null);
    setLandingChips(new Set());
  }, [snapKey]);

  // drop chips cleared externally
  useEffect(() => {
    setPlacedChips(prev => {
      let changed = false;
      const next = new Map(prev);
      for (const [wid] of next) {
        if (!values[wid]) { next.delete(wid); changed = true; }
      }
      if (changed) { placedChipsRef.current = next; }
      return changed ? next : prev;
    });
  }, [values]);

  const { setNodeRef } = useDroppable({ id: axisDropId });

  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    axisRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);

  const snapFromEvent = useCallback((translated: { left: number; width: number }): number | null => {
    if (!axisRef.current) return null;
    const axisRect = axisRef.current.getBoundingClientRect();
    if (axisRect.width === 0) return null; // not yet laid out
    const chipCenterX = translated.left + translated.width / 2;
    const raw         = pixelToValue(chipCenterX, axisRect.left, axisRect.width, min, max);
    return snapToNearest(raw, snapTicks);
  }, [min, max, snapTicks]);

  // float-safe; excludeWid = chip being re-dragged (its tick treated as free)
  const isTickOccupied = useCallback(
    (tick: number, excludeWid?: string): boolean =>
      [...placedChipsRef.current.entries()].some(
        ([wid, c]) => wid !== excludeWid && almostEqual(c.position, tick),
      ),
    [], // reads placedChipsRef (a ref — stable, no dep needed)
  );

  // reads placedChipsRef directly — always sync-up-to-date
  const findNearestFreeWidgetSlotCb = useCallback(
    (axisValue: number, excludeWid?: string) =>
      findNearestFreeWidgetSlot(positions, axisValue, values, placedChipsRef.current, excludeWid),
    [positions, values],
  );

  const triggerLanding = useCallback((widgetId: string) => {
    setLandingChips(prev => new Set(prev).add(widgetId));
    clearTimeout(landingTimersRef.current.get(widgetId));
    landingTimersRef.current.set(widgetId, setTimeout(() => {
      setLandingChips(prev => { const s = new Set(prev); s.delete(widgetId); return s; });
      landingTimersRef.current.delete(widgetId);
    }, 300));
  }, []);

  useDndMonitor({
    onDragMove(event) {
      if (!event.over || event.over.id !== axisDropId) {
        setHoverSnap(null);
        return;
      }
      const translated = event.active.rect.current.translated;
      if (!translated) return;
      setHoverSnap(snapFromEvent(translated));
    },
    onDragCancel() { setHoverSnap(null); },
    onDragEnd(event) {
      setHoverSnap(null);
      if (!event.over || event.over.id !== axisDropId) return;
      if (!axisRef.current) return;

      const translated = event.active.rect.current.translated;
      if (!translated) return;

      const rawPlaced = snapFromEvent(translated);
      if (rawPlaced === null) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[FreeDragAxis] snap calculation failed — axis may not be laid out yet');
        }
        return;
      }
      const placed = cleanFloat(rawPlaced);

      const data = event.active.data.current as Record<string, unknown>;
      if (!data) return;

      // re-drag
      if (data.type === 'placed-chip') {
        const oldWidgetId = data.widgetId as string;
        const label       = data.label as string;
        const sourceId    = placedChipsRef.current.get(oldWidgetId)?.sourceId ?? oldWidgetId;

        if (isTickOccupied(placed, oldWidgetId)) return;
        const targetPos = findNearestFreeWidgetSlotCb(placed, oldWidgetId);
        if (!targetPos) return;

        const newWidgetId = getPositionWidgetId(targetPos.content);
        if (!newWidgetId) return;

        if (newWidgetId !== oldWidgetId) {
          onChangeRef.current(oldWidgetId, '', false, sourceId); // clear old slot
        }
        onChangeRef.current(newWidgetId, encodeNLDragValue(label, placed), true, sourceId);
        setPlacedChips(prev => {
          const next = new Map(prev);
          if (newWidgetId !== oldWidgetId) next.delete(oldWidgetId);
          next.set(newWidgetId, { label, position: placed, sourceId });
          placedChipsRef.current = next;
          return next;
        });
        triggerLanding(newWidgetId);
        return;
      }

      // bank drop
      if (data.type !== 'drag-option') return;
      if (isTickOccupied(placed)) return;

      const targetPos = findNearestFreeWidgetSlotCb(placed);
      if (!targetPos) return;

      const targetWidgetId = getPositionWidgetId(targetPos.content);
      if (!targetWidgetId) return;

      const chipLabel = (data.data as DragOptionData).value;
      const sourceId  = String(event.active.id);
      onChangeRef.current(targetWidgetId, encodeNLDragValue(chipLabel, placed), true, sourceId);
      setPlacedChips(prev => {
        const next = new Map(prev).set(targetWidgetId, { label: chipLabel, position: placed, sourceId });
        placedChipsRef.current = next;
        return next;
      });
      triggerLanding(targetWidgetId);
    },
  });

  return (
    <div
      ref={mergedRef}
      className="number-line-axis-row nl-axis-drag-mode"
    >
      <div className="number-line-track" />

      {showArrowLeft  && <div className="number-line-arrow number-line-arrow-left"  aria-hidden="true" />}
      {showArrowRight && <div className="number-line-arrow number-line-arrow-right" aria-hidden="true" />}

      <HighlightBands highlights={highlights} min={min} max={max} />

      {/* Cursor fill bands and balls */}
      <CursorBalls
        cursors={cursors}
        min={min}
        max={max}
        snapTicks={snapTicks}
        audibleTicks={majorTicks}
        axisRowRef={axisRef}
        onChange={onChange}
        values={values}
        cursorValidationStatuses={cursorValidationStatuses}
      />

      {minorValues.map(v => (
        <div
          key={`minor-${cleanFloat(v)}`}
          className={`number-line-minor-tick${hoverSnap === v ? ' nl-tick-hover' : ''}`}
          style={{ left: `${toPercent(v, min, max)}%` }}
        />
      ))}

      {majorTicks.map(tick => (
        <div
          key={`major-${cleanFloat(tick)}`}
          className={`number-line-major-tick${hoverSnap === tick ? ' nl-tick-hover' : ''}`}
          style={{ left: `${toPercent(tick, min, max)}%` }}
        />
      ))}

      {/* snap preview */}
      {hoverSnap !== null && (
        <div
          className="nl-snap-preview"
          style={{ left: `${toPercent(hoverSnap, min, max)}%` }}
          aria-hidden="true"
        />
      )}

      {/* context markers — no drop target */}
      {positions
        .filter(pos => getPositionWidgetId(pos.content) === null)
        .map(pos => (
          <div
            key={`dot-${pos.id}`}
            className="number-line-position-dot"
            style={{ left: `${toPercent(pos.value, min, max)}%` }}
          />
        ))}

      {positions.map(pos => {
        const wid  = getPositionWidgetId(pos.content);
        const chip = wid ? placedChips.get(wid) : undefined;
        if (!wid || !chip) return null;
        return (
          <PlacedChip
            key={wid}
            widgetId={wid}
            label={chip.label}
            leftPercent={toPercent(chip.position, min, max)}
            isLanding={landingChips.has(wid)}
            onRemove={() => {
              onChangeRef.current(wid, '', false, chip.sourceId);
              setPlacedChips(prev => {
                const next = new Map(prev);
                next.delete(wid);
                placedChipsRef.current = next;
                return next;
              });
            }}
          />
        );
      })}
    </div>
  );
};